import { stopActiveDictation } from '@/lib/dictation/active-dictation';

import {
  clearActiveTts,
  registerActiveTts,
  setActiveTtsContext,
  stopActiveTts,
} from './active-tts';
import {
  buildGlobalOccurrenceMap,
  buildWordSegments,
  countTtsWords,
  findCurrentWordIndex,
} from './alignment-utils';
import { TTS_STOP_REASONS, type TtsStopReason } from './analytics-constants';
import { readNdjsonStream, type NdjsonChunk } from './ndjson-stream';
import { toTtsText } from './text-extraction-loader';
import { reportTtsPlaybackError, updateTtsWordInfo } from './tts-signal';

import type { TtsVoiceSettings, WordSegment } from './types';
import type { SpeechSynthesisAdapter } from '@assistant-ui/react';

const DEFAULT_TTS_ENDPOINT = '/api/tts';
const DEFAULT_VOLUME = 0.65;
export const TTS_PROVIDER = 'elevenlabs' as const;

// Tiny silent WAV used to "unlock" the Audio element inside the click gesture.
// iOS Safari requires audio.play() to be initiated synchronously from a user
// gesture; once an element has been play()-ed in that context, subsequent
// src swaps + play() calls from async code are allowed on the same element.
const SILENT_AUDIO_DATA_URI =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAAAAAAAA';

/**
 * Pick the best constructor for progressive MP3 playback at runtime.
 *
 * Selection order:
 *   1. `ManagedMediaSource` — iOS / Safari 17.1+. Classic `MediaSource` is
 *      NOT exposed for `<audio>` in iOS Safari, which is what was forcing
 *      us into the slow buffer-the-whole-stream fallback before this fix.
 *   2. `MediaSource` — supported on desktop browsers and Android Chrome.
 *   3. `null` — caller falls back to `playBuffered` (older iOS, etc.).
 *
 * Exported with a `_` prefix to signal it's internal/test-only — the unit
 * tests stub the constructors and verify the selection logic without
 * spinning up a full streaming Response.
 *
 * @returns The chosen constructor (typed as `typeof MediaSource` because
 *   ManagedMediaSource is a structural subclass), or `null` if neither is
 *   available or neither supports `audio/mpeg`.
 */
export function _pickStreamingMediaSourceCtor(): typeof MediaSource | null {
  if (typeof window === 'undefined') return null;
  const MMS = window.ManagedMediaSource;
  if (MMS && MMS.isTypeSupported('audio/mpeg')) {
    return MMS as unknown as typeof MediaSource;
  }
  if (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg')) {
    return MediaSource;
  }
  return null;
}

export type TtsAnalyticsEvent = 'tts_started' | 'tts_ended';

export interface ElevenLabsSpeechAdapterOptions {
  voiceId?: string;
  modelId?: string;
  voiceSettings?: TtsVoiceSettings;
  volume?: number;
  ttsEndpoint?: string;
  localeRef: { current: string };
  threadIdRef?: { current: string | null };
  onAnalyticsEvent?: (event: TtsAnalyticsEvent, properties: Record<string, unknown>) => void;
}

interface TrackingRefs {
  segments: WordSegment[];
  occurrences: number[];
  // Highest segment index the rAF tracker has reached. Used as `wordsPlayed`
  // in the tts_ended analytics event so partial playbacks are measurable.
  maxIdx: number;
}

interface PlaybackHandlers {
  isCancelled: () => boolean;
  onObjectUrl: (url: string) => void;
  onMediaSource: (ms: MediaSource) => void;
  onTrackerCancel: (fn: () => void) => void;
  onSourceBufferTeardown: (fn: () => void) => void;
  onStarted: () => void;
  onEnded: () => void;
  onError: (err: unknown) => void;
}

const b64ToUint8 = (b64: string): Uint8Array<ArrayBuffer> =>
  Uint8Array.from(atob(b64), c => c.charCodeAt(0));

function appendAlignment(
  chunk: NdjsonChunk,
  buf: { chars: string[]; starts: number[]; ends: number[] },
  trackingRefs: TrackingRefs,
): boolean {
  if (!chunk.c || !chunk.s || !chunk.e) return false;
  buf.chars.push(...chunk.c);
  buf.starts.push(...chunk.s);
  buf.ends.push(...chunk.e);
  const segments = buildWordSegments({
    characters: buf.chars,
    characterStartTimesSeconds: buf.starts,
    characterEndTimesSeconds: buf.ends,
  });
  trackingRefs.segments = segments;
  trackingRefs.occurrences = buildGlobalOccurrenceMap(segments);
  return true;
}

// rAF loop: tracks current word, holding previous index during inter-word gaps,
// and updates the global TTS signal. Records the highest segment index reached
// on `trackingRefs.maxIdx` for analytics.
function startWordTracker(audioEl: HTMLAudioElement, trackingRefs: TrackingRefs): () => void {
  let active = true;
  let rafId = 0;
  let currentSegIdx = -1;

  const tick = () => {
    if (!active) return;
    const { segments, occurrences } = trackingRefs;
    if (segments.length > 0) {
      const rawIdx = findCurrentWordIndex({ segments, currentTime: audioEl.currentTime });
      // Hold the previous word during inter-word gaps to prevent flickering.
      const isInGap = rawIdx === -1 && currentSegIdx >= 0 && currentSegIdx < segments.length - 1;
      const idx = isInGap ? currentSegIdx : rawIdx;
      if (idx !== currentSegIdx) {
        currentSegIdx = idx;
        if (idx > trackingRefs.maxIdx) trackingRefs.maxIdx = idx;
        updateTtsWordInfo(
          idx >= 0 ? { word: segments[idx].word, occurrence: occurrences[idx] } : null,
        );
      }
    }
    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);
  return () => {
    active = false;
    cancelAnimationFrame(rafId);
  };
}

export class ElevenLabsSpeechAdapter implements SpeechSynthesisAdapter {
  private readonly voiceId?: string;
  private readonly modelId?: string;
  private readonly voiceSettings?: TtsVoiceSettings;
  private readonly volume: number;
  private readonly ttsEndpoint: string;
  private readonly localeRef: { current: string };
  private readonly threadIdRef?: { current: string | null };
  private readonly onAnalyticsEvent?: ElevenLabsSpeechAdapterOptions['onAnalyticsEvent'];

  constructor({
    voiceId,
    modelId,
    voiceSettings,
    volume = DEFAULT_VOLUME,
    ttsEndpoint = DEFAULT_TTS_ENDPOINT,
    localeRef,
    threadIdRef,
    onAnalyticsEvent,
  }: ElevenLabsSpeechAdapterOptions) {
    this.voiceId = voiceId;
    this.modelId = modelId;
    this.voiceSettings = voiceSettings;
    this.volume = volume;
    this.ttsEndpoint = ttsEndpoint;
    this.localeRef = localeRef;
    this.threadIdRef = threadIdRef;
    this.onAnalyticsEvent = onAnalyticsEvent;
  }

  speak(rawMarkdown: string): SpeechSynthesisAdapter.Utterance {
    stopActiveDictation();
    // Cancel any in-flight TTS from a different message so we don't end up
    // with two concurrent <audio> elements + stale active-tts pointer.
    stopActiveTts(TTS_STOP_REASONS.USER_STOPPED);

    // Process raw markdown (may be multiple parts joined with "\n\n") into TTS text.
    const ttsText = toTtsText(rawMarkdown);
    const locale = this.localeRef.current;
    const threadId = this.threadIdRef?.current ?? undefined;
    const wordCount = countTtsWords(ttsText);
    const characterCount = ttsText.length;
    const startedAt = Date.now();

    const baseAnalyticsProps = {
      threadId,
      provider: TTS_PROVIDER,
      voiceId: this.voiceId,
      modelId: this.modelId,
      locale,
      wordCount,
      characterCount,
    };

    this.onAnalyticsEvent?.('tts_started', baseAnalyticsProps);

    let status: SpeechSynthesisAdapter.Status = { type: 'starting' };
    const listeners = new Set<() => void>();
    const notify = () => listeners.forEach(l => l());
    const setStatus = (s: SpeechSynthesisAdapter.Status) => {
      status = s;
      notify();
    };

    // Create + prime the Audio element synchronously inside the click gesture
    // so iOS Safari marks it as user-activated. `muted` keeps the silent
    // priming inaudible; we restore volume + unmute right before swapping in
    // the real source. Event handlers are attached later (in the playback
    // path) so the prime's onplay/onended don't fire premature state changes.
    let audio: HTMLAudioElement | null = new Audio();
    audio.setAttribute('playsinline', 'true');
    audio.preload = 'auto';
    audio.muted = true;
    audio.src = SILENT_AUDIO_DATA_URI;
    // `play()` returns a Promise in real browsers, undefined in older engines
    // and jsdom. Optional chain swallows both shapes without an extra alloc.
    audio.play()?.catch(() => {});

    let objectUrl: string | null = null;
    let mediaSource: MediaSource | null = null;
    let cancelTracker: (() => void) | null = null;
    let sourceBufferTeardown: (() => void) | null = null;
    let cancelled = false;
    let terminated = false;
    const abortController = new AbortController();

    const trackingRefs: TrackingRefs = { segments: [], occurrences: [], maxIdx: -1 };

    const cleanup = () => {
      cancelTracker?.();
      cancelTracker = null;
      sourceBufferTeardown?.();
      sourceBufferTeardown = null;
      if (audio) {
        audio.onplay = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.src = '';
        audio = null;
      }
      if (mediaSource?.readyState === 'open') {
        try {
          mediaSource.endOfStream();
        } catch {}
      }
      mediaSource = null;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    const fireEnded = (reason: TtsStopReason, errorType?: string) => {
      if (terminated) return;
      terminated = true;
      this.onAnalyticsEvent?.('tts_ended', {
        ...baseAnalyticsProps,
        durationMs: Date.now() - startedAt,
        wordsPlayed: trackingRefs.maxIdx + 1,
        reason,
        ...(errorType && { errorType }),
      });
      setActiveTtsContext(null);
    };

    const cancel = (reason: TtsStopReason = TTS_STOP_REASONS.USER_STOPPED) => {
      if (cancelled) return;
      cancelled = true;
      abortController.abort();
      cleanup();
      clearActiveTts();
      updateTtsWordInfo(null);
      fireEnded(reason);
      setStatus({ type: 'ended', reason: 'cancelled' });
    };

    registerActiveTts(cancel);

    setActiveTtsContext({
      startTime: startedAt,
      staticProperties: baseAnalyticsProps,
      getDynamicProperties: () => ({ wordsPlayed: trackingRefs.maxIdx + 1 }),
    });

    const handlers: PlaybackHandlers = {
      isCancelled: () => cancelled,
      onObjectUrl: url => {
        objectUrl = url;
      },
      onMediaSource: ms => {
        mediaSource = ms;
      },
      onTrackerCancel: fn => {
        cancelTracker = fn;
      },
      onSourceBufferTeardown: fn => {
        sourceBufferTeardown = fn;
      },
      onStarted: () => setStatus({ type: 'running' }),
      onEnded: () => {
        cleanup();
        clearActiveTts();
        updateTtsWordInfo(null);
        fireEnded(TTS_STOP_REASONS.COMPLETED);
        setStatus({ type: 'ended', reason: 'finished' });
      },
      onError: (err: unknown) => {
        if (terminated) return;
        console.error('[TTS]', err);
        cleanup();
        clearActiveTts();
        updateTtsWordInfo(null);
        const errorType = err instanceof Error ? err.message : 'unknown';
        fireEnded(TTS_STOP_REASONS.ERROR, errorType);
        reportTtsPlaybackError();
        setStatus({ type: 'ended', reason: 'error', error: err });
      },
    };

    void this.streamAndPlay({
      audioEl: audio,
      ttsText,
      trackingRefs,
      signal: abortController.signal,
      handlers,
    });

    return {
      get status() {
        return status;
      },
      cancel: () => cancel(TTS_STOP_REASONS.USER_STOPPED),
      subscribe(cb) {
        listeners.add(cb);
        return () => listeners.delete(cb);
      },
    };
  }

  private async streamAndPlay({
    audioEl,
    ttsText,
    trackingRefs,
    signal,
    handlers,
  }: {
    audioEl: HTMLAudioElement;
    ttsText: string;
    trackingRefs: TrackingRefs;
    signal: AbortSignal;
    handlers: PlaybackHandlers;
  }): Promise<void> {
    if (!ttsText.trim()) {
      handlers.onEnded();
      return;
    }

    let response: Response;
    try {
      response = await fetch(this.ttsEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal,
        body: JSON.stringify({
          text: ttsText,
          ...(this.voiceId && { voiceId: this.voiceId }),
          ...(this.modelId && { modelId: this.modelId }),
          ...(this.voiceSettings && { voiceSettings: this.voiceSettings }),
          ...(this.localeRef.current && { languageCode: this.localeRef.current }),
        }),
      });
    } catch (e) {
      if (handlers.isCancelled()) return;
      handlers.onError(e instanceof Error ? e : new Error('TTS fetch failed'));
      return;
    }

    if (handlers.isCancelled()) return;

    if (!response.ok) {
      handlers.onError(new Error(`TTS request failed: ${response.status}`));
      return;
    }

    const streamingCtor = response.body ? _pickStreamingMediaSourceCtor() : null;
    if (streamingCtor) {
      await this.playWithMediaSource({ audioEl, response, trackingRefs, handlers, streamingCtor });
    } else {
      await this.playBuffered({ audioEl, response, trackingRefs, handlers });
    }
  }

  private prepareAudioElement({
    audioEl,
    url,
    handlers,
  }: {
    audioEl: HTMLAudioElement;
    url: string;
    handlers: PlaybackHandlers;
  }): void {
    audioEl.pause();
    audioEl.muted = false;
    audioEl.volume = this.volume;
    audioEl.onplay = () => handlers.onStarted();
    audioEl.onended = () => handlers.onEnded();
    audioEl.onerror = () => {
      const { error } = audioEl;
      console.error(`[TTS] Audio error: code=${error?.code} message=${error?.message}`);
      handlers.onError(new Error('TTS playback error'));
    };
    audioEl.src = url;
  }

  private async startAudioPlayback({
    audioEl,
    handlers,
  }: {
    audioEl: HTMLAudioElement;
    handlers: PlaybackHandlers;
  }): Promise<void> {
    try {
      await audioEl.play();
    } catch (err) {
      if (!handlers.isCancelled()) handlers.onError(err);
    }
  }

  private async playWithMediaSource({
    audioEl,
    response,
    trackingRefs,
    handlers,
    streamingCtor,
  }: {
    audioEl: HTMLAudioElement;
    response: Response;
    trackingRefs: TrackingRefs;
    handlers: PlaybackHandlers;
    streamingCtor: typeof MediaSource;
  }): Promise<void> {
    const ms = new streamingCtor();
    // WebKit requires AirPlay remote-playback to be disabled before attaching
    // a ManagedMediaSource — otherwise `sourceopen` never fires. The setter
    // is a no-op on browsers that ignore it. We detect via the `streaming`
    // property the instance carries (only present on ManagedMediaSource).
    if ('streaming' in ms) {
      audioEl.disableRemotePlayback = true;
    }
    handlers.onMediaSource(ms);
    const url = URL.createObjectURL(ms);
    handlers.onObjectUrl(url);
    this.prepareAudioElement({ audioEl, url, handlers });

    let sourceBuffer: SourceBuffer | null = null;
    const pendingBuffers: Uint8Array<ArrayBuffer>[] = [];
    let isAppending = false;
    let streamEnded = false;

    const onUpdateEnd = () => {
      isAppending = false;
      flushPending();
    };
    const onSourceBufferError = () => handlers.onError(new Error('MediaSource buffer error'));

    handlers.onSourceBufferTeardown(() => {
      sourceBuffer?.removeEventListener('updateend', onUpdateEnd);
      sourceBuffer?.removeEventListener('error', onSourceBufferError);
    });

    const flushPending = () => {
      if (isAppending || !sourceBuffer || sourceBuffer.updating) return;
      if (pendingBuffers.length > 0) {
        isAppending = true;
        sourceBuffer.appendBuffer(pendingBuffers.shift()!);
      } else if (streamEnded && ms.readyState === 'open') {
        try {
          ms.endOfStream();
        } catch {}
      }
    };

    ms.addEventListener(
      'sourceopen',
      () => {
        sourceBuffer = ms.addSourceBuffer('audio/mpeg');
        sourceBuffer.addEventListener('updateend', onUpdateEnd);
        sourceBuffer.addEventListener('error', onSourceBufferError);
        flushPending();
      },
      { once: true },
    );

    // Start playback as soon as the first audio chunk arrives — don't wait
    // for usable alignment data. `startWordTracker` safely no-ops while
    // `trackingRefs.segments` is empty, so word highlighting simply catches
    // up once alignment arrives in a subsequent chunk. Previously we gated
    // on `appendAlignment(...) === true`, which requires the chunk to carry
    // a complete `c`/`s`/`e` triple — partial alignment on the first chunk
    // would push audible start back by 1+ chunks.
    let playbackStarted = false;
    const startPlayback = () => {
      if (playbackStarted || handlers.isCancelled()) return;
      playbackStarted = true;
      handlers.onTrackerCancel(startWordTracker(audioEl, trackingRefs));
      void this.startAudioPlayback({ audioEl, handlers });
    };

    const buf = { chars: [] as string[], starts: [] as number[], ends: [] as number[] };

    const streamOk = await readNdjsonStream(response.body!, (chunk: NdjsonChunk) => {
      if (handlers.isCancelled()) return;
      if (chunk.a) {
        pendingBuffers.push(b64ToUint8(chunk.a));
        flushPending();
        startPlayback();
      }
      appendAlignment(chunk, buf, trackingRefs);
    });

    if (handlers.isCancelled()) return;

    if (!streamOk) {
      handlers.onError(new Error('TTS stream read failed'));
      return;
    }

    startPlayback();
    streamEnded = true;
    flushPending();
  }

  private async playBuffered({
    audioEl,
    response,
    trackingRefs,
    handlers,
  }: {
    audioEl: HTMLAudioElement;
    response: Response;
    trackingRefs: TrackingRefs;
    handlers: PlaybackHandlers;
  }): Promise<void> {
    const binaryChunks: Uint8Array<ArrayBuffer>[] = [];
    const buf = { chars: [] as string[], starts: [] as number[], ends: [] as number[] };

    const streamOk = await readNdjsonStream(response.body!, (chunk: NdjsonChunk) => {
      if (chunk.a) binaryChunks.push(b64ToUint8(chunk.a));
      appendAlignment(chunk, buf, trackingRefs);
    });

    if (handlers.isCancelled()) return;

    if (!streamOk) {
      handlers.onError(new Error('TTS stream read failed'));
      return;
    }

    const totalLen = binaryChunks.reduce((sum, a) => sum + a.length, 0);
    const combined = new Uint8Array(totalLen);
    let offset = 0;
    for (const chunk of binaryChunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }

    const blob = new Blob([combined], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);
    handlers.onObjectUrl(url);
    this.prepareAudioElement({ audioEl, url, handlers });

    handlers.onTrackerCancel(startWordTracker(audioEl, trackingRefs));
    await this.startAudioPlayback({ audioEl, handlers });
  }
}
